import { useMutation } from '@tanstack/react-query';
import { toast } from '@quinyx/ui';

import i18n from '@/i18n';
import {
  getSupportTask,
  deleteSupportTask,
} from '../../../api/endpoints/support/task';
import { IErrorResponse } from '../../../api/interfaces';
import {
  ISupportTask,
  IDeleteTaskResponse,
} from '../../../api/interfaces/support/task';
import { ACCESS_DENIED_STATUS_CODES } from '../../../constants/http';
import { NOT_FOUND_STATUS_CODE } from '../../../constants/support-task';

const getErrorMessage = (error: IErrorResponse) => {
  const status = error?.response?.status;
  const message = error?.response?.data?.message;

  if (status && ACCESS_DENIED_STATUS_CODES.includes(status)) {
    return i18n.t('toast.taskAccessDenied');
  }

  return message || i18n.t('toast.somethingWentWrongTryAgain');
};

const notifyError = (error: IErrorResponse) =>
  toast({ message: getErrorMessage(error), type: 'negative' });

const notifyErrorUnlessNotFound = (error: IErrorResponse) => {
  if (error?.response?.status === NOT_FOUND_STATUS_CODE) {
    return;
  }
  notifyError(error);
};

const notifySuccess = (message: string) =>
  toast({ message: i18n.t(message), type: 'positive' });

export const useGetSupportTask = () => {
  const {
    mutateAsync: findTask,
    isPending: isFinding,
    data: task,
    error: findError,
    variables,
    reset: resetTask,
  } = useMutation<
    ISupportTask,
    IErrorResponse,
    { taskId: string; domainId: string }
  >({
    mutationFn: ({ taskId, domainId }) => getSupportTask(taskId, domainId),
    onError: notifyErrorUnlessNotFound,
    useErrorBoundary: false,
  });

  const isNotFound = findError?.response?.status === NOT_FOUND_STATUS_CODE;

  return {
    findTask,
    task,
    isFinding,
    isNotFound,
    resetTask,
    lookedUpTaskId: variables?.taskId,
  };
};

export const useDeleteSupportTask = () => {
  const {
    mutateAsync: deleteTask,
    isPending: isDeleting,
    data: deleteResult,
    reset: resetDelete,
  } = useMutation<
    IDeleteTaskResponse,
    IErrorResponse,
    { taskId: string; reason: string; ticket: string; domainId: string }
  >({
    mutationFn: ({ taskId, reason, ticket, domainId }) =>
      deleteSupportTask(taskId, reason, ticket, domainId),
    onSuccess: () => notifySuccess('toast.taskDeleted'),
    onError: notifyError,
    useErrorBoundary: false,
  });

  return { deleteTask, deleteResult, isDeleting, resetDelete };
};
